import { ISftpPartnerSummary } from "types/SftpPartner";

import { formatDateTime } from "../../utils/hstImports";
import { compactAge, relativeTime } from "../../utils/sftpPartners";

// A daily sweep that has not stamped the partner for this long has stopped running (or is failing
// before the end of its pass).
const STALE_SWEEP_MS = 36 * 60 * 60 * 1000;

type IQueueFields = Pick<
  ISftpPartnerSummary,
  "queue" | "lastSweepAt" | "filesWaitingInBucket"
>;

/** True when the Queue cell has anything to say. */
export const hasPendingWork = ({ queue, filesWaitingInBucket }: IQueueFields) =>
  (queue?.notProcessed ?? 0) > 0 ||
  (queue?.failed ?? 0) > 0 ||
  (filesWaitingInBucket ?? 0) > 0;

/**
 * CLE-2950 — the Queue column of Integrations → SFTP. Blank when nothing is pending. Otherwise: the
 * partner's files not processed yet (held split pieces, parses and claim creation not finished) with the
 * oldest one's age, in warning style; files that failed and need a reprocess, in red; and the files the
 * last sweep left in the bucket, which exist in no table. Counted from each file's own state, not from
 * job history, so a file a later reprocess fixed is never shown as failed.
 */
export function SftpQueueCell({
  partner,
  now = new Date(),
}: {
  partner: IQueueFields;
  now?: Date;
}) {
  if (!hasPendingWork(partner)) return null;
  const { queue, filesWaitingInBucket, lastSweepAt } = partner;
  const notProcessed = queue?.notProcessed ?? 0;
  const failed = queue?.failed ?? 0;
  const age = compactAge(queue?.oldestNotProcessedAt ?? null, now);

  return (
    <div className="flex flex-column gap-1" data-testid="sftp-queue-cell">
      {notProcessed > 0 ? (
        <span className="text-orange-600 font-semibold">
          {`${notProcessed.toLocaleString()} not processed${
            age ? ` · oldest ${age}` : ""
          }`}
        </span>
      ) : null}
      {failed > 0 ? (
        <span className="text-red-600 font-semibold">
          {`${failed.toLocaleString()} failed · needs reprocess`}
        </span>
      ) : null}
      {(filesWaitingInBucket ?? 0) > 0 ? (
        <span className="text-orange-600 font-semibold">
          {(filesWaitingInBucket ?? 0).toLocaleString()} waiting since{" "}
          {formatDateTime(lastSweepAt)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * When the inbound sweep last finished a pass over the partner. Orange once it is more than a day and a
 * half old on an enabled partner with an inbound job, so a sweep that stopped running stands out.
 */
export function SftpLastSweep({
  partner,
  now = new Date(),
}: {
  partner: Pick<ISftpPartnerSummary, "lastSweepAt" | "ingestKind" | "enabled">;
  now?: Date;
}) {
  if (!partner.lastSweepAt) {
    return (
      <span className="text-600">
        {partner.ingestKind === "NONE" ? "—" : "Not yet"}
      </span>
    );
  }
  const stale =
    partner.enabled &&
    partner.ingestKind !== "NONE" &&
    now.getTime() - new Date(partner.lastSweepAt).getTime() > STALE_SWEEP_MS;
  return (
    <span
      title={formatDateTime(partner.lastSweepAt)}
      className={stale ? "text-orange-600 font-semibold" : undefined}
      data-testid="sftp-last-sweep"
    >
      {relativeTime(partner.lastSweepAt, now)}
    </span>
  );
}
